// ---------------------------------------------------------------------------
// debugFlag — the single switch for diagnostic output.
//
// (Morgan, Aug 27 2026.) Every `[pool]`, `[raw]`, `[perf]`, `[pleasant-final]`
// and `[scenic-score]` line was a bare console.log, and vite.config.ts has no
// `drop_console`, so the entire routing pipeline — provider identities,
// scoring internals, gate names, rejection reasons, curated road names — was
// printing in production for every user on every search. Anyone who opened
// DevTools on pleasantroutes.com read the whole thing. No user data was
// exposed; the app's reasoning was.
//
// The fix is a runtime gate, not a build-time strip: `drop_console` would
// delete the logs from the production bundle entirely and `?debug=1` would
// have nothing left to turn on — which would blind the watchdog and every
// future production diagnosis. So the calls stay in the bundle and simply do
// nothing unless diagnostics are switched on.
//
// Semantics deliberately match the existing DebugOverlay toggle in
// Index.tsx (?debug=1 sets, ?debug=0 clears, localStorage remembers) so
// nothing about how you turn diagnostics on changes.
// ---------------------------------------------------------------------------

/** True when this session should emit diagnostic console output. */
export function diagnosticsEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const sp = new URLSearchParams(window.location.search);
    const debug = sp.get("debug");
    if (debug === "1") {
      localStorage.setItem("pleasant.debug", "1");
      return true;
    }
    if (debug === "0") localStorage.removeItem("pleasant.debug");
    if (localStorage.getItem("pleasant.debug") === "1") return true;
    // productionShadow runs on its own flag and its output is the point of
    // the run — keep it audible whenever shadow recording is on, so the
    // watchdog and the shadow harness are unaffected.
    if (sp.get("shadow") === "1") return true;
    if (localStorage.getItem("pleasant.shadow.v1") === "1") return true;
    return false;
  } catch {
    return false;
  }
}

/** Silence console.log/info/debug unless diagnostics are on. Errors and
 *  warnings are always kept — they are how real failures reach Sentry and
 *  how a user's bug report stays useful. Development builds are untouched. */
export function installDiagnosticsGate(): void {
  if (typeof window === "undefined") return;
  if (import.meta.env.DEV) return;
  if (diagnosticsEnabled()) return;
  const noop = () => {};
  console.log = noop;
  console.info = noop;
  console.debug = noop;
}
